import React, { Children } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, ClipboardCheck, Clock, MapPin, ChevronRight, CheckCircle, PlayCircle } from 'lucide-react';
export function DeepDivePage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const lessons = [{
    week: 1,
    title: 'Introduction to Romans & The Gospel',
    status: 'completed'
  }, {
    week: 2,
    title: 'The Universal Need for Salvation',
    status: 'completed'
  }, {
    week: 3,
    title: 'Justification by Faith Alone',
    status: 'current'
  }, {
    week: 4,
    title: 'Freedom from Sin and the Law',
    status: 'upcoming'
  }, {
    week: 5,
    title: 'Life in the Spirit',
    status: 'upcoming'
  }, {
    week: 6,
    title: "God's Sovereign Choice",
    status: 'upcoming'
  }];
  return <Layout data-id="element-548">
      {/* Hero Section */}
      <section className="relative pt-44 md:pt-48 pb-24 bg-[#0A1F44] overflow-hidden text-center" data-id="element-549">
        <div className="absolute inset-0 opacity-40 mix-blend-overlay" data-id="element-550">
          <img src="https://images.unsplash.com/photo-1491841550275-ad7854e35ca6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Bible Study" className="w-full h-full object-cover" data-id="element-551" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/80 via-[#0A1F44]/40 to-[#0A1F44]/80" data-id="element-552" />

        <div className="container-custom relative z-10" data-id="element-553">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex flex-col items-center" data-id="element-554">
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-8 backdrop-blur-sm border border-white/20" data-id="element-555">
              <BookOpen className="w-10 h-10 text-[#E5D3B3]" data-id="element-556" />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-557">
              Deep Dive <span className="text-[#fc0719]" data-id="element-558">Bible Study</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-10 font-serif italic" data-id="element-559">
              Dig deeper into God's Word. Grow in knowledge, wisdom, and
              spiritual maturity.
            </p>
            <Link to="/visit" data-id="element-560">
              <Button variant="primary" size="lg" className="font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(200,16,46,0.4)] px-10 py-4" data-id="element-561">
                Join Next Session
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-562">
        <div className="container-custom" data-id="element-563">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-564">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-565">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-566">
                What is Deep Dive?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-567">
                Deep Dive is our dedicated Bible Study experience designed to
                help you dig deeper into Scripture in a focused, intimate
                setting. Every Sunday morning before our main service, we gather
                to study God's Word together, ask questions, share insights, and
                grow alongside others who are hungry for God's truth.
              </p>

              <div className="space-y-6 bg-white p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-568">
                <div className="flex items-center gap-4" data-id="element-569">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-570">
                    <Clock className="w-6 h-6 text-[#fc0719]" data-id="element-571" />
                  </div>
                  <div data-id="element-572">
                    <h4 className="font-bold text-[#0A1F44] text-lg" data-id="element-573">
                      When We Meet
                    </h4>
                    <p className="text-gray-600" data-id="element-574">
                      Every Sunday Morning @ 9:00 AM
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-575">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-576">
                    <MapPin className="w-6 h-6 text-[#fc0719]" data-id="element-577" />
                  </div>
                  <div data-id="element-578">
                    <h4 className="font-bold text-[#0A1F44] text-lg" data-id="element-579">
                      Where We Meet
                    </h4>
                    <p className="text-gray-600" data-id="element-580">
                      TheHousehold Church (Main Sanctuary & Classrooms)
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-581">
              <div className="rounded-2xl overflow-hidden shadow-2xl w-full h-[500px] object-cover" data-id="element-582">
                <img src="https://images.unsplash.com/photo-1504052434569-70ad5836ab65?auto=format&fit=crop&q=80" alt="Bible Study" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" data-id="element-583" />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#fc0719]/10 rounded-full blur-2xl -z-10" data-id="element-584" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-20 bg-white" data-id="element-585">
        <div className="container-custom" data-id="element-586">
          <div className="text-center mb-16" data-id="element-587">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-588">
              Current Series
            </h2>
            <div className="h-1 w-20 bg-[#fc0719] mx-auto rounded-full" data-id="element-589" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-590">
            <div className="bg-[#F5F7FA] p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow duration-300" data-id="element-591">
              <div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6" data-id="element-592">
                <BookOpen className="w-8 h-8 text-[#0A1F44]" data-id="element-593" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-4 uppercase tracking-wide" data-id="element-594">
                Bible Study Lessons
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-595">
                Structured, weekly teachings that break down complex biblical
                concepts into practical, applicable truth for everyday life.
              </p>
              <ul className="space-y-4" data-id="element-596">
                <li className="flex items-start" data-id="element-597">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-598" />{' '}
                  <span data-id="element-599">The Nature of God</span>
                </li>
                <li className="flex items-start" data-id="element-600">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-601" />{' '}
                  <span data-id="element-602">The Power of Prayer</span>
                </li>
                <li className="flex items-start" data-id="element-603">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-604" />{' '}
                  <span data-id="element-605">Walking in the Spirit</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#F5F7FA] p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow duration-300" data-id="element-606">
              <div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6" data-id="element-607">
                <FileText className="w-8 h-8 text-[#0A1F44]" data-id="element-608" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-4 uppercase tracking-wide" data-id="element-609">
                Materials & Resources
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-610">
                Access comprehensive study guides, handouts, and recommended
                reading to support your personal study time during the week.
              </p>
              <ul className="space-y-4" data-id="element-611">
                <li className="flex items-start" data-id="element-612">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-613" />{' '}
                  <span data-id="element-614">Weekly Study Outlines</span>
                </li>
                <li className="flex items-start" data-id="element-615">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-616" />{' '}
                  <span data-id="element-617">Recommended Reading Lists</span>
                </li>
                <li className="flex items-start" data-id="element-618">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-619" />{' '}
                  <span data-id="element-620">Digital Resource Library</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#F5F7FA] p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow duration-300" data-id="element-621">
              <div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6" data-id="element-622">
                <ClipboardCheck className="w-8 h-8 text-[#0A1F44]" data-id="element-623" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-4 uppercase tracking-wide" data-id="element-624">
                Assessments
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-625">
                Track your spiritual growth and understanding through gentle
                reflection questions, quizzes, and practical application steps.
              </p>
              <ul className="space-y-4" data-id="element-626">
                <li className="flex items-start" data-id="element-627">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-628" />{' '}
                  <span data-id="element-629">Weekly Reflection Questions</span>
                </li>
                <li className="flex items-start" data-id="element-630">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-631" />{' '}
                  <span data-id="element-632">Knowledge Check Quizzes</span>
                </li>
                <li className="flex items-start" data-id="element-633">
                  <ChevronRight className="w-4 h-4 text-[#fc0719] mr-2" data-id="element-634" />{' '}
                  <span data-id="element-635">Spiritual Growth Tracking</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Current Study Section */}
      <section className="py-20 bg-[#0A1F44] text-white" data-id="element-636">
        <div className="container-custom" data-id="element-637">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-638">
            <div className="lg:col-span-4" data-id="element-639">
              <div className="sticky top-32" data-id="element-640">
                <span className="inline-block py-1 px-3 rounded-full bg-[#fc0719]/20 text-[#fc0719] font-bold text-sm tracking-widest uppercase mb-4 border border-[#fc0719]/30" data-id="element-641">
                  Current Series
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-[#E5D3B3]" data-id="element-642">
                  The Book of Romans
                </h2>
                <p className="text-white/80 text-lg mb-8 leading-relaxed" data-id="element-643">
                  Join us as we explore Paul's masterpiece on the gospel, grace,
                  and what it means to be justified by faith. This 6-week series
                  will transform your understanding of salvation.
                </p>
                <Button variant="primary" className="w-full sm:w-auto shadow-[0_0_15px_rgba(200,16,46,0.3)]" data-id="element-644">
                  Download Study Guide
                </Button>
              </div>
            </div>

            <div className="lg:col-span-8" data-id="element-645">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-10 backdrop-blur-sm" data-id="element-646">
                <h3 className="text-2xl font-bold mb-8 border-b border-white/10 pb-4" data-id="element-647">
                  Lesson Schedule
                </h3>

                <div className="space-y-4" data-id="element-648">
                  {lessons.map((lesson, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: index * 0.1
                }} className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-xl border transition-all duration-300 ${lesson.status === 'current' ? 'bg-[#163A70] border-[#4A90E2] shadow-lg scale-[1.02]' : lesson.status === 'completed' ? 'bg-white/5 border-white/10 opacity-70' : 'bg-white/5 border-white/10'}`} data-id="element-649">
                      <div className="flex items-center gap-4 mb-4 sm:mb-0" data-id="element-650">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 font-bold text-lg ${lesson.status === 'current' ? 'bg-[#4A90E2] text-white' : 'bg-white/10 text-white/70'}`} data-id="element-651">
                          W{lesson.week}
                        </div>
                        <h4 className={`text-lg font-bold ${lesson.status === 'completed' ? 'line-through text-white/50' : 'text-white'}`} data-id="element-652">
                          {lesson.title}
                        </h4>
                      </div>

                      <div className="flex items-center gap-3 sm:ml-4" data-id="element-653">
                        {lesson.status === 'completed' && <span className="flex items-center text-green-400 text-sm font-bold uppercase tracking-wider" data-id="element-654">
                            <CheckCircle className="w-4 h-4 mr-1.5" data-id="element-655" /> Completed
                          </span>}
                        {lesson.status === 'current' && <span className="flex items-center text-[#4A90E2] text-sm font-bold uppercase tracking-wider bg-[#4A90E2]/10 px-3 py-1 rounded-full" data-id="element-656">
                            <PlayCircle className="w-4 h-4 mr-1.5" data-id="element-657" /> This
                            Sunday
                          </span>}
                        {lesson.status === 'upcoming' && <span className="text-white/40 text-sm font-bold uppercase tracking-wider" data-id="element-658">
                            Upcoming
                          </span>}
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-[#F5F7FA] text-center" data-id="element-659">
        <div className="container-custom" data-id="element-660">
          <h2 className="text-4xl md:text-5xl font-script text-[#0A1F44] mb-6" data-id="element-661">
            Ready to Go Deeper?
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-662">
            Join us this Sunday at 9:00 AM. No registration required — just
            bring your Bible and a hungry heart.
          </p>
          <Link to="/visit" data-id="element-663">
            <Button variant="primary" size="lg" className="font-bold tracking-widest uppercase px-10 py-4 shadow-lg" data-id="element-664">
              Plan Your Visit
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}